import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, CalendarClock } from "lucide-react";
import { api, apiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BADGE = {
  pending_confirmation: { label: "Awaiting studio confirmation", cls: "bg-amber-100 text-amber-800" },
  confirmed: { label: "Booking confirmed", cls: "bg-emerald-100 text-emerald-700" },
  reschedule_requested: { label: "Reschedule requested", cls: "bg-rose-100 text-rose-700" },
};

export function BookingCard({ job, user, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showR, setShowR] = useState(false);
  const [resc, setResc] = useState({ suggested_date: "", note: "" });
  const [newDate, setNewDate] = useState("");

  if (!job.booking_status) return null;
  const role = user?.role;
  const isStudio = ["studio_manager", "studio_owner", "network_admin", "network_owner"].includes(role);
  const isCentral = ["central_sales_team", "network_admin", "network_owner"].includes(role);
  const badge = BADGE[job.booking_status] || {};

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); onChanged?.(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const confirm = () => call(async () => { await api.post(`/jobs/${job.id}/booking/confirm`); toast.success("Booking confirmed"); });
  const reschedule = () => {
    if (!resc.suggested_date) return toast.error("Pick a suggested date");
    call(async () => { await api.post(`/jobs/${job.id}/booking/reschedule`, resc); toast.success("Reschedule requested — Central Sales notified"); setShowR(false); });
  };
  const rebook = (d) => {
    if (!d) return toast.error("Pick a date");
    call(async () => { await api.post(`/jobs/${job.id}/booking/rebook`, { booked_date: d }); toast.success("Re-booked — studio notified"); setNewDate(""); });
  };

  return (
    <Card className="mt-4 border-brand-line shadow-none" data-testid="booking-card">
      <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarCheck className="h-4 w-4 text-brand" /> Central Sales booking
        </CardTitle>
        <Badge className={`${badge.cls} border-0`} data-testid="booking-status-badge">{badge.label}</Badge>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="grid grid-cols-2 gap-2">
          <div><span className="text-slate-500">Booked date</span><div className="font-medium" data-testid="booking-date">{fmtDate(job.booked_date)}</div></div>
          <div><span className="text-slate-500">Booked by</span><div className="font-medium">{job.booked_by || "Central Sales"}</div></div>
        </div>

        {job.booking_status === "reschedule_requested" && (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-3" data-testid="booking-suggested">
            Studio suggested <b>{fmtDate(job.reschedule_suggested_date)}</b>
            {job.reschedule_note ? ` — ${job.reschedule_note}` : ""}.
          </div>
        )}

        {/* Studio manager actions */}
        {isStudio && job.booking_status === "pending_confirmation" && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={confirm} disabled={busy} data-testid="booking-confirm-btn">
              <CalendarCheck className="mr-1.5 h-4 w-4" /> Confirm booking
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowR((v) => !v)} data-testid="booking-reschedule-toggle">
              <CalendarClock className="mr-1.5 h-4 w-4" /> Request reschedule
            </Button>
          </div>
        )}
        {isStudio && showR && job.booking_status === "pending_confirmation" && (
          <div className="grid gap-2 rounded-md border border-brand-line p-3 sm:grid-cols-[auto,1fr,auto]">
            <div><Label className="text-xs">Suggested date</Label>
              <Input type="date" className="mt-1" data-testid="booking-suggest-date"
                value={resc.suggested_date} onChange={(e) => setResc((r) => ({ ...r, suggested_date: e.target.value }))} /></div>
            <div><Label className="text-xs">Note (optional)</Label>
              <Input className="mt-1" value={resc.note} onChange={(e) => setResc((r) => ({ ...r, note: e.target.value }))} /></div>
            <div className="flex items-end"><Button size="sm" onClick={reschedule} disabled={busy} data-testid="booking-reschedule-submit">Send</Button></div>
          </div>
        )}

        {/* Central Sales re-book */}
        {isCentral && job.booking_status === "reschedule_requested" && (
          <div className="flex flex-wrap items-end gap-2">
            <Button size="sm" onClick={() => rebook(job.reschedule_suggested_date)} disabled={busy} data-testid="booking-accept-suggested">
              Re-book on {fmtDate(job.reschedule_suggested_date)}
            </Button>
            <div><Label className="text-xs">Or a different date</Label>
              <Input type="date" className="mt-1" data-testid="booking-new-date" value={newDate} onChange={(e) => setNewDate(e.target.value)} /></div>
            <Button size="sm" variant="outline" onClick={() => rebook(newDate)} disabled={busy} data-testid="booking-rebook-btn">Re-book</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
